import {z} from 'zod';

import {isNil} from 'ramda';

// Embedded Signup reports its result to the opening window through postMessage.
// https://developers.facebook.com/docs/whatsapp/embedded-signup/implementation

const HTTPS_PREFIX = 'https://';
const FACEBOOK_DOMAIN = 'facebook.com';

// facebook.com or any of its subdomains over HTTPS (www, web, business, …), nothing else.
// An origin is `scheme://host[:port]`, so a port or a look-alike domain fails the suffix check.
const isFacebookOrigin = (origin: string) => {
  if (!origin.startsWith(HTTPS_PREFIX)) {
    return false;
  }
  const host = origin.slice(HTTPS_PREFIX.length);
  return host === FACEBOOK_DOMAIN || host.endsWith(`.${FACEBOOK_DOMAIN}`);
};

const finishEventSchema = z.object({
  type: z.literal('WA_EMBEDDED_SIGNUP'),
  event: z.enum(['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING']),
  data: z.object({
    waba_id: z.string().min(1),
    // Coexistence onboarding sends only the WABA; the backend then uses its only number.
    phone_number_id: z.string().min(1).optional(),
  }),
});

const cancelEventSchema = z.object({
  type: z.literal('WA_EMBEDDED_SIGNUP'),
  event: z.enum(['CANCEL', 'ERROR']),
  data: z
    .object({
      current_step: z.string().optional(),
      error_code: z.union([z.string(), z.number()]).optional(),
    })
    .optional(),
});

export type EmbeddedSignupMessage =
  | {type: 'finished'; wabaId: string; phoneNumberId: string | null}
  | {type: 'cancelled'; currentStep: string | null; errorCode: string | null};

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Returns the Embedded Signup result carried by a window message, or null when the message
 * does not come from Facebook or is not an Embedded Signup result.
 */
export function parseEmbeddedSignupMessage(
  event: Pick<MessageEvent, 'origin' | 'data'>
): EmbeddedSignupMessage | null {
  if (!isFacebookOrigin(event.origin) || typeof event.data !== 'string') {
    return null;
  }

  const payload = parseJson(event.data);

  const finish = finishEventSchema.safeParse(payload);
  if (finish.success) {
    return {
      type: 'finished',
      wabaId: finish.data.data.waba_id,
      phoneNumberId: finish.data.data.phone_number_id ?? null,
    };
  }

  const cancel = cancelEventSchema.safeParse(payload);
  if (cancel.success) {
    const errorCode = cancel.data.data?.error_code;
    return {
      type: 'cancelled',
      currentStep: cancel.data.data?.current_step ?? null,
      errorCode: isNil(errorCode) ? null : errorCode.toString(),
    };
  }

  return null;
}
